import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-monokai';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';

import { getPartnerGuid, hasAuthourity } from '../../lib/access.es6';
import {
  getIprsConfigsRequest,
  getIprsConfigRawRequest,
  getIprsConfigRawReset,
  publishIprsConfigRequest,
  publishIprsConfigReset,
  createIprsConfigRequest,
  createIprsConfigReset,
} from './actions.es6';

var TH = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};
var TD = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

function newIprsConfigTemplate(partner) {
  return [
    'partner: ' + partner,
    'type: iprs',
    'enabled: true',
  ].join('\n');
}

class IprsConfigs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editorOpen: false,
      isNew: false,
      selectedConfig: null,
      editorContent: '',
      hasYamlErrors: false,
    };
  }

  componentWillMount() {
    this.props.dispatch(getIprsConfigsRequest(getPartnerGuid()));
  }

  componentDidUpdate(prevProps) {
    var iprs_config_raw = this.props.iprs_config_raw;
    var successfully_published = this.props.successfully_published;
    var successfully_created = this.props.successfully_created;

    if (iprs_config_raw && iprs_config_raw !== prevProps.iprs_config_raw) {
      this.setState({ editorContent: iprs_config_raw });
    }
    if (successfully_published && successfully_published !== prevProps.successfully_published) {
      this.closeEditor();
    }
    if (successfully_created && successfully_created !== prevProps.successfully_created) {
      this.closeEditor();
    }
  }

  openEdit(config) {
    this.props.dispatch(getIprsConfigRawReset());
    this.props.dispatch(publishIprsConfigReset());
    this.setState({ editorOpen: true, isNew: false, selectedConfig: config, editorContent: '' });
    this.props.dispatch(getIprsConfigRawRequest(config.partner, config.type));
  }

  openCreate() {
    this.props.dispatch(getIprsConfigRawReset());
    this.props.dispatch(createIprsConfigReset());
    this.setState({
      editorOpen: true,
      isNew: true,
      selectedConfig: null,
      editorContent: newIprsConfigTemplate(getPartnerGuid()),
    });
  }

  closeEditor() {
    this.setState({ editorOpen: false, selectedConfig: null, editorContent: '' });
    this.props.dispatch(getIprsConfigRawReset());
    this.props.dispatch(publishIprsConfigReset());
    this.props.dispatch(createIprsConfigReset());
  }

  handlePublish() {
    var editorContent = this.state.editorContent;
    var isNew = this.state.isNew;
    var selectedConfig = this.state.selectedConfig;
    var partner = getPartnerGuid();
    if (isNew) {
      this.props.dispatch(createIprsConfigRequest(partner, editorContent));
    } else {
      this.props.dispatch(publishIprsConfigRequest(partner, selectedConfig.type, editorContent));
    }
  }

  render() {
    if (!hasAuthourity('VIEW_UNDERWRITING_FLOWS')) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view IPRS Configs.
          </Typography>
        </Box>
      );
    }

    var loader = this.props.loader;
    var iprs_configs = this.props.iprs_configs;
    var get_configs_error = this.props.get_configs_error;
    var config_raw_loader = this.props.config_raw_loader;
    var get_config_raw_error = this.props.get_config_raw_error;
    var publish_loader = this.props.publish_loader;
    var publish_error = this.props.publish_error;
    var create_loader = this.props.create_loader;
    var create_error = this.props.create_error;

    var editorOpen = this.state.editorOpen;
    var isNew = this.state.isNew;
    var selectedConfig = this.state.selectedConfig;
    var editorContent = this.state.editorContent;
    var hasYamlErrors = this.state.hasYamlErrors;

    var saving = publish_loader || create_loader;
    var saveError = publish_error || create_error;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FingerprintOutlinedIcon sx={{ color: '#6B7280' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              IPRS Configs
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.openCreate()}
            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
          >
            New Config
          </Button>
        </Box>

        {get_configs_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load IPRS configs. Please try again.
          </Alert>
        )}

        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Partner</TableCell>
                  <TableCell sx={TH}>Type</TableCell>
                  <TableCell sx={TH}>Enabled</TableCell>
                  <TableCell sx={TH} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!iprs_configs || iprs_configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ ...TD, textAlign: 'center', color: '#9CA3AF', py: 4 }}>
                      No IPRS configs found for this partner.
                    </TableCell>
                  </TableRow>
                ) : (
                  iprs_configs.map(function(config) {
                    return (
                      <TableRow key={config.partner + ':' + config.type} hover>
                        <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{config.partner}</TableCell>
                        <TableCell sx={{ ...TD, fontWeight: 500 }}>
                          <Chip label={config.type} size="small" sx={{ fontSize: 11 }} />
                        </TableCell>
                        <TableCell sx={TD}>
                          <Chip
                            label={config.enabled !== false ? 'enabled' : 'disabled'}
                            size="small"
                            color={config.enabled !== false ? 'success' : 'default'}
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell sx={TD} align="right">
                          <Tooltip title="Edit YAML">
                            <IconButton size="small" onClick={() => this.openEdit(config)} sx={{ color: '#0D47A1' }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  }.bind(this))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={editorOpen}
          onClose={() => this.closeEditor()}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '85vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isNew ? 'New IPRS Config' : (selectedConfig && selectedConfig.partner + ' — ' + selectedConfig.type)}
              </Typography>
              {!isNew && selectedConfig && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6B7280' }}>
                  {selectedConfig.partner}:{selectedConfig.type}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => this.closeEditor()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            {saveError && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                {(saveError && saveError.message) || 'Failed to save config. Check your YAML is valid.'}
              </Alert>
            )}
            {get_config_raw_error && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                Failed to load config content.
              </Alert>
            )}

            {config_raw_loader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <AceEditor
                  mode="yaml"
                  theme="monokai"
                  value={editorContent}
                  onChange={value => this.setState({ editorContent: value })}
                  onValidate={annotations => this.setState({ hasYamlErrors: annotations.some(function(a) { return a.type === 'error'; }) })}
                  name="iprs-config-yaml-editor"
                  width="100%"
                  height="100%"
                  fontSize={13}
                  showPrintMargin={false}
                  showGutter={true}
                  highlightActiveLine={true}
                  editorProps={{ $blockScrolling: true }}
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: false,
                    showLineNumbers: true,
                    tabSize: 2,
                    useWorker: true,
                  }}
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
            <Button onClick={() => this.closeEditor()} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
              onClick={() => this.handlePublish()}
              disabled={saving || config_raw_loader || !editorContent || hasYamlErrors}
              sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
            >
              {saving ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    loader: state.iprsConfigs.loader,
    iprs_configs: state.iprsConfigs.iprs_configs,
    get_configs_error: state.iprsConfigs.get_configs_error,

    config_raw_loader: state.iprsConfigs.config_raw_loader,
    iprs_config_raw: state.iprsConfigs.iprs_config_raw,
    get_config_raw_error: state.iprsConfigs.get_config_raw_error,

    publish_loader: state.iprsConfigs.publish_loader,
    successfully_published: state.iprsConfigs.successfully_published,
    publish_error: state.iprsConfigs.publish_error,

    create_loader: state.iprsConfigs.create_loader,
    successfully_created: state.iprsConfigs.successfully_created,
    create_error: state.iprsConfigs.create_error,
  };
}

export default connect(mapStateToProps)(IprsConfigs);
